import type { MusicCue } from './musicAnalysis';
// Turns analyser band levels into what the games need each frame: how loud each
// band is, and a beat clock the games can build on.
//
// The clock is a steady tempo grid. It starts at the song's tempo and always
// ticks, so a game never waits on detection to get content. While music plays,
// onsets found by spectral flux pull the grid's tempo and phase onto the real
// beat. Games spawn on `beat` (a grid tick) and can use `onset` for extra hits.
// Deciding beats from raw level jumps does not work on mastered music: it is
// compressed so hard that the bass barely moves, which left the games empty.

export interface BandInput {
  kickLevel: number;
  bassLevel: number;
  midLevel: number;
  trebleLevel: number;
  overallLevel: number;
}

export const SPECTRUM_BANDS = 32;

export interface AudioFrame {
  musicCue?: (aheadSeconds: number) => MusicCue;
  kick: number;
  bass: number;
  mid: number;
  treble: number;
  energy: number;
  // A grid tick landed this frame. Always on tempo, heard or not.
  beat: boolean;
  beatIndex: number;
  pulse: number;
  sinceBeat: number;
  beatPeriod: number;
  // Milliseconds until the next grid tick, for scheduling notes onto beats.
  nextBeatIn: number;
  // A hit the analyser actually heard this frame (live music only).
  onset: boolean;
  onsetStrength: number;
  live: boolean;
  spectrum: Float32Array;
}

export const MIN_BEAT_GAP_MS = 260;
const MIN_BPM = 70;
const MAX_BPM = 180;
const SILENCE_LEVEL = 0.015;
const SILENCE_GRACE_MS = 1200;
const PULSE_DECAY_MS = 190;
const ONSET_GAP_MS = 140;
const FLUX_WINDOW = 48;
const ONSET_MEMORY_MS = 8000;
const PHASE_PULL = 0.18;
const TEMPO_PULL = 0.25;

const clamp01 = (value: number) => (value < 0 ? 0 : value > 1 ? 1 : value);

// Folds a beat period into the playable range, so a half or double time
// reading lands on the same grid.
export function foldPeriod(periodMs: number): number {
  const low = 60000 / MAX_BPM;
  const high = 60000 / MIN_BPM;
  let period = periodMs;
  while (period < low) period *= 2;
  while (period > high) period /= 2;
  return period;
}

// Best tempo for a list of onset times: every interval between onsets up to
// four apart votes for its folded period, weighted by the hits' strength.
export function estimatePeriod(onsets: ReadonlyArray<{ at: number; strength: number }>, around: number): number | null {
  if (onsets.length < 6) return null;
  const bucketMs = 10;
  const votes = new Map<number, number>();
  for (let i = 0; i < onsets.length; i++) {
    for (let j = i + 1; j < Math.min(onsets.length, i + 5); j++) {
      const gap = onsets[j].at - onsets[i].at;
      if (gap < 150 || gap > 2400) continue;
      for (const steps of [1, 2, 3, 4]) {
        const period = gap / steps;
        if (period < 60000 / MAX_BPM || period > 60000 / MIN_BPM) continue;
        const bucket = Math.round(period / bucketMs);
        // Short, adjacent intervals say more about the beat than long ones.
        const weight = (onsets[i].strength + onsets[j].strength) / (steps * (j - i));
        votes.set(bucket, (votes.get(bucket) ?? 0) + weight);
      }
    }
  }
  let best: number | null = null;
  let bestScore = 0;
  for (const [bucket, raw] of votes) {
    const smoothed = raw + 0.5 * ((votes.get(bucket - 1) ?? 0) + (votes.get(bucket + 1) ?? 0));
    // Mild preference for staying near the current tempo keeps the grid calm.
    const period = bucket * bucketMs;
    const nearness = 1 / (1 + Math.abs(period - around) / around);
    const score = smoothed * (0.75 + 0.25 * nearness);
    if (score > bestScore) {
      bestScore = score;
      best = period;
    }
  }
  return best;
}

// Where in the beat the onsets land, as ms after a multiple of `period`. The
// strongest cluster wins, so off-beat hats do not drag the grid off the kick.
export function estimatePhase(onsets: ReadonlyArray<{ at: number; strength: number }>, period: number): number | null {
  if (onsets.length < 4) return null;
  const bucketCount = 24;
  const buckets = new Float64Array(bucketCount);
  for (const onset of onsets) {
    const phase = ((onset.at % period) + period) % period;
    buckets[Math.floor((phase / period) * bucketCount) % bucketCount] += onset.strength;
  }
  let best = 0;
  let bestScore = -1;
  for (let i = 0; i < bucketCount; i++) {
    const score = buckets[i] + 0.5 * (buckets[(i + 1) % bucketCount] + buckets[(i + bucketCount - 1) % bucketCount]);
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }
  // Refine inside the winning bucket with the onsets that fell there.
  let sum = 0;
  let weight = 0;
  for (const onset of onsets) {
    const phase = ((onset.at % period) + period) % period;
    let offset = phase - ((best + 0.5) / bucketCount) * period;
    if (offset > period / 2) offset -= period;
    if (offset < -period / 2) offset += period;
    if (Math.abs(offset) <= (period / bucketCount) * 1.5) {
      sum += offset * onset.strength;
      weight += onset.strength;
    }
  }
  const center = ((best + 0.5) / bucketCount) * period + (weight > 0 ? sum / weight : 0);
  return ((center % period) + period) % period;
}

export interface BeatTracker {
  update(levels: BandInput | null, nowMs: number, fallbackBpm: number, bins?: ArrayLike<number> | null, tempoLocked?: boolean): AudioFrame;
  reset(): void;
}

export function createBeatTracker(): BeatTracker {
  const spectrum = new Float32Array(SPECTRUM_BANDS);
  let previousBins: Float32Array | null = null;
  const flux: number[] = [];
  let onsets: Array<{ at: number; strength: number }> = [];
  let lastOnset = -Infinity;

  let period = 600;
  let seededBpm = 0;
  let nextBeat: number | null = null;
  let lastBeat = -Infinity;
  let beatIndex = 0;
  let heardAt = -Infinity;
  let lastTempoFit = 0;
  let clockStart = 0;
  let lastNow: number | null = null;
  let fixedTempo = false;

  // Callers can hand over a different clock (the demo warms a game up on a
  // made-up timeline, then switches to performance.now()). When time runs
  // backwards, move the whole timeline with it instead of reporting a
  // negative time since the beat, which made the pulse explode.
  const rebase = (nowMs: number) => {
    if (lastNow !== null && nowMs < lastNow) {
      const shift = nowMs - lastNow;
      if (nextBeat !== null) nextBeat += shift;
      lastBeat += shift;
      lastOnset += shift;
      heardAt += shift;
      lastTempoFit += shift;
      clockStart += shift;
      for (const onset of onsets) onset.at += shift;
    }
    lastNow = nowMs;
  };

  // Moves the grid forward to now. Returns true when at least one tick passed.
  const advance = (nowMs: number) => {
    if (nextBeat === null) {
      nextBeat = nowMs;
      clockStart = nowMs;
    }
    let ticked = false;
    while (nowMs >= nextBeat) {
      lastBeat = nextBeat;
      nextBeat += period;
      beatIndex += 1;
      ticked = true;
    }
    return ticked;
  };

  const detectOnset = (bins: ArrayLike<number>, nowMs: number) => {
    const usable = Math.max(8, Math.floor(bins.length * 0.6));
    if (!previousBins || previousBins.length !== usable) previousBins = new Float32Array(usable);
    let sum = 0;
    for (let i = 0; i < usable; i++) {
      const value = (bins[i] ?? 0) / 255;
      const rise = value - previousBins[i];
      // Low bins carry the kick and the snare body; weight them up.
      if (rise > 0) sum += rise * (i < usable * 0.15 ? 2.2 : i < usable * 0.45 ? 1.2 : 0.7);
      previousBins[i] = value;
    }
    const value = sum / usable;
    flux.push(value);
    if (flux.length > FLUX_WINDOW) flux.shift();
    if (flux.length < 8) return 0;
    let mean = 0;
    for (const f of flux) mean += f;
    mean /= flux.length;
    let variance = 0;
    for (const f of flux) variance += (f - mean) ** 2;
    const deviation = Math.sqrt(variance / flux.length);
    const threshold = mean + deviation * 1.4 + 0.002;
    if (value <= threshold || nowMs - lastOnset < ONSET_GAP_MS) return 0;
    lastOnset = nowMs;
    return clamp01((value - mean) / (deviation * 4 + 0.004));
  };

  // Pulls the grid onto what was heard: phase toward each onset near a tick,
  // tempo toward the best fit over the last few seconds.
  const lockTo = (at: number, strength: number) => {
    onsets.push({ at, strength });
    onsets = onsets.filter((onset) => at - onset.at < ONSET_MEMORY_MS);
    if (nextBeat === null) return;
    const previous = nextBeat - period;
    const nearest = at - previous < nextBeat - at ? previous : nextBeat;
    const error = at - nearest;
    if (Math.abs(error) < period * 0.3) nextBeat += error * PHASE_PULL * (0.5 + strength * 0.5);
    if (at - lastTempoFit > 1000) {
      lastTempoFit = at;
      const fit = fixedTempo ? null : estimatePeriod(onsets, period);
      if (fit) period = foldPeriod(period + (fit - period) * TEMPO_PULL);
      // The pull above only reaches onsets near a tick. A grid that started
      // half a beat off needs the whole phase moved: find where recent onsets
      // cluster within the beat and jump there.
      const phase = estimatePhase(onsets.filter((onset) => at - onset.at < 5000), period);
      if (phase !== null && nextBeat !== null) {
        const current = ((nextBeat % period) + period) % period;
        let shift = phase - current;
        if (shift > period / 2) shift -= period;
        if (shift < -period / 2) shift += period;
        if (Math.abs(shift) > period * 0.12) {
          const moved = nextBeat + shift;
          // Never squeeze a tick in right after the last one.
          nextBeat = moved - lastBeat < period * 0.45 ? moved + period : moved;
        }
      }
    }
  };

  const frame = (nowMs: number, values: Pick<AudioFrame, "kick" | "bass" | "mid" | "treble" | "energy" | "live"> & { beat: boolean; onset: number }): AudioFrame => {
    const since = Math.max(0, nowMs - lastBeat);
    return {
      kick: values.kick,
      bass: values.bass,
      mid: values.mid,
      treble: values.treble,
      energy: values.energy,
      beat: values.beat,
      beatIndex,
      pulse: Number.isFinite(since) ? Math.exp(-since / PULSE_DECAY_MS) : 0,
      sinceBeat: since,
      beatPeriod: period,
      nextBeatIn: nextBeat === null ? period : Math.max(0, nextBeat - nowMs),
      onset: values.onset > 0,
      onsetStrength: values.onset,
      live: values.live,
      spectrum,
    };
  };

  return {
    update(levels, nowMs, fallbackBpm, bins, tempoLocked = false) {
      const newlyLocked = tempoLocked && !fixedTempo;
      fixedTempo = tempoLocked;
      rebase(nowMs);
      const bpm = Math.max(MIN_BPM * 0.5, fallbackBpm || 100);
      if (bpm !== seededBpm || newlyLocked) {
        // A new song: start from its own tempo.
        seededBpm = bpm;
        period = foldPeriod(60000 / bpm);
        onsets = [];
      }
      if (levels && levels.overallLevel > SILENCE_LEVEL) heardAt = nowMs;
      const hearing = levels !== null && nowMs - heardAt < SILENCE_GRACE_MS;

      if (!levels || !hearing) {
        // Practice beat: the same grid, with levels drawn from it.
        const beat = advance(nowMs);
        const since = Math.max(0, nowMs - lastBeat);
        const pulse = Math.exp(-since / PULSE_DECAY_MS);
        const elapsed = nowMs - clockStart;
        const sway = Math.sin(elapsed / 900);
        const kick = clamp01(0.15 + pulse * 0.7);
        const bass = clamp01(0.3 + pulse * 0.35 + sway * 0.05);
        const mid = clamp01(0.32 + Math.sin(elapsed / 640) * 0.12);
        const treble = clamp01(0.22 + Math.sin(elapsed / 410 + 1.3) * 0.1 + (beatIndex % 2 === 1 ? pulse * 0.15 : 0));
        for (let i = 0; i < SPECTRUM_BANDS; i++) {
          const falloff = 1 - i / SPECTRUM_BANDS;
          spectrum[i] = clamp01(falloff * (0.25 + pulse * 0.55) + Math.sin(elapsed / 300 + i * 0.7) * 0.08 + 0.08);
        }
        return frame(nowMs, { kick, bass, mid, treble, energy: (kick + bass + mid + treble) / 4, live: false, beat, onset: beat ? 0.6 : 0 });
      }

      let onset = 0;
      if (bins && bins.length > 0) {
        onset = detectOnset(bins, nowMs);
        const usable = Math.floor(bins.length * 0.7);
        for (let i = 0; i < SPECTRUM_BANDS; i++) {
          const from = Math.floor(Math.pow(i / SPECTRUM_BANDS, 1.6) * usable);
          const to = Math.max(from + 1, Math.floor(Math.pow((i + 1) / SPECTRUM_BANDS, 1.6) * usable));
          let sum = 0;
          for (let j = from; j < to; j++) sum += bins[j] ?? 0;
          spectrum[i] = clamp01(sum / (to - from) / 255);
        }
      }
      if (onset > 0) lockTo(nowMs, onset);
      const beat = advance(nowMs);

      return frame(nowMs, {
        kick: clamp01(levels.kickLevel),
        bass: clamp01(levels.bassLevel),
        mid: clamp01(levels.midLevel),
        treble: clamp01(levels.trebleLevel),
        energy: clamp01(levels.overallLevel),
        live: true,
        beat,
        onset,
      });
    },
    reset() {
      previousBins = null;
      flux.length = 0;
      onsets = [];
      lastOnset = -Infinity;
      nextBeat = null;
      lastBeat = -Infinity;
      beatIndex = 0;
      heardAt = -Infinity;
      seededBpm = 0;
      lastTempoFit = 0;
      lastNow = null;
    },
  };
}

export function bpmFromPeriod(periodMs: number): number {
  return Math.round(60000 / Math.max(1, periodMs));
}
